"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.AxiosHttpService = void 0;
const axios_1 = __importDefault(require("axios"));
const messaging_common_1 = require("@guided-methods/messaging-common");
const generate_random_bytes_1 = require("../utils/generate-random-bytes");
class AxiosHttpService {
    constructor(headers, logger, tokenService) {
        this.headers = headers;
        this.logger = logger;
        this.tokenService = tokenService;
    }
    async get(url) {
        try {
            const correlationId = this.generateXrayTraceId();
            const headers = await this.makeHeaders(this.headers, correlationId);
            const tokenInfo = await this.getTokenInfo();
            const logger = (0, messaging_common_1.enrichLogger)(this.logger, { correlationId: correlationId, ...tokenInfo });
            logger.info(`Outcoming: GET ${url}, headers: ${this.outputHeaders(headers)}`);
            return await axios_1.default.get(url, { headers });
        }
        catch (e) {
            throw new Error(`GET '${url}' failed with code: ${e instanceof Error ? e.message : "Unknown error"}`);
        }
    }
    async put(url, data = undefined) {
        try {
            const correlationId = this.generateXrayTraceId();
            const headers = await this.makeHeaders(this.headers, correlationId);
            const tokenInfo = await this.getTokenInfo();
            const logger = (0, messaging_common_1.enrichLogger)(this.logger, { correlationId: correlationId, ...tokenInfo });
            logger.info(`Outcoming: PUT ${url}, headers: ${this.outputHeaders(headers)}`);
            return await axios_1.default.put(url, data, { headers });
        }
        catch (e) {
            throw new Error(`PUT '${url}' failed with code: ${e instanceof Error ? e.message : "Unknown error"}`);
        }
    }
    async post(url, data, correlationId = undefined) {
        try {
            const corrId = correlationId ?? this.getCorrelationIdFromContext(data) ?? this.generateXrayTraceId();
            const headers = await this.makeHeaders(this.headers, corrId);
            const tokenInfo = await this.getTokenInfo();
            const logger = (0, messaging_common_1.enrichLogger)(this.logger, { correlationId: corrId, ...tokenInfo });
            logger.info({
                message: `Outcoming: POST ${url}, headers: ${this.outputHeaders(headers)},
      data: ${data?.type === 'DATA' && data.to?.endsWith('writemode')
                    ? '*** write-mode ***'
                    : JSON.stringify(data)}`,
                ...data?.context
            });
            return await axios_1.default.post(url, data, { headers });
        }
        catch (e) {
            throw new Error(`POST to '${url}' failed with code: ${e instanceof Error ? e.message : "Unknown error"}`);
        }
    }
    outputHeaders(headers) {
        const safeHeaders = JSON.stringify(headers.Authorization ? { ...headers, Authorization: '***' } : headers);
        return safeHeaders;
    }
    async makeHeaders(headers, correlationId) {
        const updatedHeaders = await this.addAuthorization(headers);
        return { ...updatedHeaders, 'X-Amzn-Trace-Id': `Root=${correlationId}` };
    }
    async addAuthorization(headers) {
        const token = await this.tokenService.getToken();
        return { ...headers, Authorization: `Bearer ${token}` };
    }
    getCorrelationIdFromContext(data) {
        try {
            let correlationId;
            const guideMessage = data;
            if (guideMessage)
                correlationId = guideMessage.context?.correlationId;
            return correlationId;
        }
        catch (error) {
            return undefined;
        }
    }
    generateXrayTraceId() {
        const datePart = Math.round(new Date().getTime() / 1000).toString(16);
        const randomPart = (0, generate_random_bytes_1.generateRandomBytes)(12);
        return `1-${datePart}-${randomPart}`;
    }
    async getTokenInfo() {
        try {
            const user = await this.tokenService.userManager.getUser();
            if (user) {
                const result = {};
                if (user.expires_at) {
                    const expiresAt = user.expires_at * 1000;
                    const now = Date.now();
                    result.tokenExpiresIn = Math.floor((expiresAt - now) / 1000);
                }
                const iat = user.profile?.iat;
                if (iat) {
                    result.tokenIssuedAt = iat;
                    result.tokenAge = Math.floor((Date.now() - iat * 1000) / 1000);
                }
                return result;
            }
            return {};
        }
        catch (error) {
            return {};
        }
    }
}
exports.AxiosHttpService = AxiosHttpService;
